import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add,
  Assignment,
  Search,
  Announcement,
  PersonAdd,
  DirectionsCar,
  Gavel,
  Assessment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const QuickActions: React.FC = () => {
  const navigate = useNavigate();

  const quickActions = [
    {
      id: 'new-booking',
      title: 'Create Booking',
      subtitle: 'Add a new booking manually',
      icon: <Add />,
      color: 'primary.main',
      action: () => navigate('/bookings/all'),
    },
    {
      id: 'view-listings',
      title: 'View Listings',
      subtitle: 'Manage vehicle listings',
      icon: <DirectionsCar />,
      color: 'success.main',
      action: () => navigate('/vehicles'),
    },
    {
      id: 'add-driver',
      title: 'Add Driver',
      subtitle: 'Onboard a new driver',
      icon: <PersonAdd />,
      color: 'info.main',
      action: () => navigate('/drivers/management'),
    },
    {
      id: 'view-disputes',
      title: 'Manage Disputes',
      subtitle: 'Handle open disputes',
      icon: <Gavel />,
      color: 'error.main',
      action: () => navigate('/disputes'),
      badge: 7,
    },
  ];

  const recentAlerts = [
    {
      id: '1',
      type: 'sla',
      title: 'SLA Breach Warning',
      message: '24 bookings at risk of missing verification deadline',
      priority: 'high',
      time: '2 hours ago',
    },
    {
      id: '2',
      type: 'document',
      title: 'Missed Document Uploads',
      message: '8 hosts failed to upload required documents within 72hr SLA',
      priority: 'medium',
      time: '4 hours ago',
    },
    {
      id: '3',
      type: 'response',
      title: 'Late Responses',
      message: '4 liaisons missed 6hr response window for booking requests',
      priority: 'medium',
      time: '6 hours ago',
    },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
      {/* Quick Actions */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Quick Actions
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {quickActions.map((action) => (
              <Button
                key={action.id}
                variant="outlined"
                fullWidth
                onClick={action.action}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  py: 1.5,
                  position: 'relative',
                }}
                startIcon={
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: action.color,
                      mr: 1,
                    }}
                  >
                    {action.icon}
                  </Avatar>
                }
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {action.subtitle}
                  </Typography>
                </Box>
                {action.badge && (
                  <Chip
                    label={action.badge}
                    size="small"
                    color="error"
                    sx={{ ml: 2 }}
                  />
                )}
              </Button>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Recent Alerts */}
      <Card sx={{ flexGrow: 1 }}>
        <CardContent sx={{ pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              SLA Alerts
            </Typography>
            <Chip
              label={`${recentAlerts.length} active`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Box>

          <List disablePadding>
            {recentAlerts.map((alert, index) => (
              <React.Fragment key={alert.id}>
                <ListItem
                  sx={{
                    px: 0,
                    py: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: `${getPriorityColor(alert.priority)}.main`,
                      }}
                    >
                      {alert.type === 'sla' && <Assignment sx={{ fontSize: 14 }} />}
                      {alert.type === 'document' && <Assessment sx={{ fontSize: 14 }} />}
                      {alert.type === 'response' && <Announcement sx={{ fontSize: 14 }} />}
                    </Avatar>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                        {alert.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          {alert.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {alert.time}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < recentAlerts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Button
            fullWidth
            variant="text"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => navigate('/alerts')}
          >
            View All Alerts
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};